import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { displayCompanyName } from '../../utils/displayName';
import { TAB_GROUPS } from '../../tabPalette';

// Click an invoice's email badge → this modal lists every email in that invoice's thread (our sent
// copies + the client's "Re:" replies), grouped by subject. Click one to read it inline.
// Replies are labelled in the Quotation / Invoice / Receipt top menu's blue (report ecfba54a); they were #1D4ED8.
type ThreadEmail = { id: string; direction: 'sent' | 'received'; fromName: string; fromEmail: string; to: string[]; date: number; subject: string; bodyHtml: string };

const fmtDate = (sec: number) => {
  if (!sec) return '';
  const d = new Date(sec * 1000); const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function InvoiceEmailThread({ invoiceId, docNo, clientName, onClose }: {
  invoiceId: string; docNo?: string; clientName?: string; onClose: () => void;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [subject, setSubject] = useState('');
  const [emails, setEmails] = useState<ThreadEmail[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.getInvoiceEmailThread(invoiceId).then((d: any) => {
      if (!alive) return;
      setSubject(d.subject || ''); setEmails(d.emails || []);
      if ((d.emails || []).length) setOpenId(d.emails[d.emails.length - 1].id);   // open the latest by default
    }).catch((e: any) => alive && setErr(e?.message || 'Failed to load')).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [invoiceId]);

  const sentN = emails.filter(e => e.direction === 'sent').length;
  const replyN = emails.filter(e => e.direction === 'received').length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10004, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 720, maxWidth: '94vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '18px 22px 12px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('iv_thread_title')}</div>
            <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 15 }}><i className="ti ti-x" /></button>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginTop: 4 }}>{docNo}{clientName ? ` · ${displayCompanyName(clientName)}` : ''}</div>
          {subject && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, fontWeight: 600 }}>{subject}</div>}
          {!loading && emails.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: '#15803D', background: '#E7F6EE', borderRadius: 999, padding: '2px 9px' }}>{t('iv_thread_sent')}: {sentN}</span>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: TAB_GROUPS.peak.color, background: '#E6F0FE', borderRadius: 999, padding: '2px 9px' }}>{t('iv_thread_reply')}: {replyN}</span>
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} /></div>
          ) : err ? (
            <div style={{ fontSize: 13, color: '#E24B4A', background: '#FCEBEB', borderRadius: 9, padding: '10px 12px' }}>{err}</div>
          ) : emails.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 40, fontSize: 13.5 }}>
              <i className="ti ti-mail-off" style={{ fontSize: 26, display: 'block', marginBottom: 8 }} />{t('iv_thread_empty')}
            </div>
          ) : emails.map(e => {
            const open = openId === e.id;
            const sent = e.direction === 'sent';
            return (
              <div key={e.id} style={{ border: '1px solid var(--border)', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
                <div onClick={() => setOpenId(open ? null : e.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', background: open ? 'var(--bg-active)' : 'transparent' }}>
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: sent ? '#15803D' : TAB_GROUPS.peak.color, background: sent ? '#E7F6EE' : '#E6F0FE', borderRadius: 6, padding: '2px 8px', flexShrink: 0 }}>
                    {sent ? t('iv_thread_sent') : t('iv_thread_reply')}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.fromName || e.fromEmail}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.fromEmail}{e.to?.length ? ` → ${e.to.join(', ')}` : ''}</div>
                  </div>
                  <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', flexShrink: 0 }}>{fmtDate(e.date)}</span>
                  <i className={`ti ti-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 14, color: 'var(--text-tertiary)', flexShrink: 0 }} />
                </div>
                {open && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '12px 14px', background: '#fff' }}>
                    <div className="rte-render" style={{ fontSize: 13.5, lineHeight: 1.6, color: '#1F2937', maxHeight: 360, overflowY: 'auto', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: e.bodyHtml || '<i>(no content)</i>' }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
